import { getPosterImageFrameStyle } from './posterFocal';
import { resolveEventPosterURL } from './posterURL';
import { EVENT_POSTER_CONSTANTS } from '@/lib/api/apiTypes';
import { Image as ExpoImage, type ImageLoadEventData } from 'expo-image';
import * as React from 'react';
import {
  Image as RNImage,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

export type FocalEventPosterImageSource = {
  public_url: string;
  position?: number;
  focal_point_x?: number;
  focal_point_y?: number;
  focal_scale?: number;
};

export function getCoverEventPosterImage(event: {
  poster_images?: FocalEventPosterImageSource[] | null;
  poster_url?: string | null;
}) {
  const coverPoster =
    event.poster_images?.reduce<FocalEventPosterImageSource | null>((currentCover, image) => {
      if (!image.public_url.trim()) {
        return currentCover;
      }
      if (!currentCover || (image.position ?? 0) < (currentCover.position ?? 0)) {
        return image;
      }
      return currentCover;
    }, null) ?? null;

  return coverPoster ?? (event.poster_url ? { public_url: event.poster_url } : null);
}

export function FocalEventPosterImage({
  className,
  image,
  onError,
  onLoadEnd,
  style,
}: {
  className?: string;
  image: FocalEventPosterImageSource;
  onError?: () => void;
  onLoadEnd?: () => void;
  style?: StyleProp<ViewStyle>;
}) {
  const [layout, setLayout] = React.useState({ width: 0, height: 0 });
  const [imageSize, setImageSize] = React.useState<{
    url: string;
    width: number;
    height: number;
  } | null>(null);
  const [loadedPosterURL, setLoadedPosterURL] = React.useState<string | null>(null);
  const reportedPosterURLRef = React.useRef<string | null>(null);
  const resolvedPosterURL = React.useMemo(
    () => resolveEventPosterURL(image.public_url),
    [image.public_url]
  );
  const activeImageSize = imageSize?.url === resolvedPosterURL ? imageSize : null;
  const focalPointX = image.focal_point_x ?? EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.x;
  const focalPointY = image.focal_point_y ?? EVENT_POSTER_CONSTANTS.FOCAL_POINT_DEFAULT.y;
  const focalScale = image.focal_scale ?? 1;

  function handleLoad(event: ImageLoadEventData) {
    setLoadedPosterURL(resolvedPosterURL);
    setImageSize((current) =>
      current?.url === resolvedPosterURL &&
      current.width === event.source.width &&
      current.height === event.source.height
        ? current
        : {
            url: resolvedPosterURL,
            width: event.source.width,
            height: event.source.height,
          }
    );
  }

  React.useEffect(() => {
    let cancelled = false;
    RNImage.getSize(
      resolvedPosterURL,
      (width, height) => {
        if (!cancelled) {
          setImageSize({ url: resolvedPosterURL, width, height });
        }
      },
      () => undefined
    );

    return () => {
      cancelled = true;
    };
  }, [resolvedPosterURL]);

  const imageFrameStyle = getPosterImageFrameStyle(
    activeImageSize?.width ?? null,
    activeImageSize?.height ?? null,
    layout.width,
    layout.height,
    focalPointX,
    focalPointY,
    focalScale
  );
  const isImageReady = Boolean(imageFrameStyle) && loadedPosterURL === resolvedPosterURL;

  React.useEffect(() => {
    if (!isImageReady || reportedPosterURLRef.current === image.public_url) {
      return;
    }

    reportedPosterURLRef.current = image.public_url;
    onLoadEnd?.();
  }, [image.public_url, isImageReady, onLoadEnd]);

  return (
    <View
      className={className}
      onLayout={(event) =>
        setLayout({
          width: event.nativeEvent.layout.width,
          height: event.nativeEvent.layout.height,
        })
      }
      style={[{ overflow: 'hidden' }, style]}>
      <ExpoImage
        source={{ uri: resolvedPosterURL }}
        contentFit={imageFrameStyle ? 'fill' : 'cover'}
        cachePolicy="memory-disk"
        onLoad={handleLoad}
        onError={onError}
        style={[
          imageFrameStyle
            ? { position: 'absolute' }
            : { height: '100%', position: 'absolute', width: '100%' },
          imageFrameStyle,
          { opacity: isImageReady ? 1 : 0 },
        ]}
      />
    </View>
  );
}
